/* =====================================================================
   UNDERSCORE SYSTEMS — THEME JAVASCRIPT
   Cart drawer, mobile nav, variant picker, predictive search,
   quantity stepper, quick-add, gallery, accessibility helpers.
   No external dependencies. Vanilla.
   ===================================================================== */

(function () {
  'use strict';

  // ---------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------
  const $ = (selector, parent = document) => parent.querySelector(selector);
  const $$ = (selector, parent = document) =>
    Array.from(parent.querySelectorAll(selector));

  const debounce = (fn, ms = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  };

  const formatMoney = (cents, currencyFormat) => {
    const dollars = (cents / 100).toFixed(2);
    if (!currencyFormat) return '$' + dollars;
    return currencyFormat.replace(/\{\{\s*amount\s*\}\}/, dollars);
  };

  const focusable =
    'a[href], button:not([disabled]), summary, input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container) {
    const getElements = () =>
      $$(focusable, container).filter((element) =>
        element.getClientRects().length && !element.closest('[inert]')
      );
    const elements = getElements();
    if (!elements.length) return () => {};
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const currentElements = getElements();
      const first = currentElements[0];
      const last = currentElements[currentElements.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    };
    container.addEventListener('keydown', handler);
    elements[0].focus();
    return () => container.removeEventListener('keydown', handler);
  }

  // ---------------------------------------------------------------------
  // Mobile menu
  // ---------------------------------------------------------------------
  class MobileMenu {
    constructor() {
      this.toggle = $('[data-mobile-menu-toggle]');
      this.menu = $('[data-mobile-menu]');
      if (!this.toggle || !this.menu) return;
      this.close = $('[data-mobile-menu-close]', this.menu);
      this.releaseFocus = null;
      this.previousOverflow = '';
      this.menu.inert = true;
      this.menu.setAttribute('aria-hidden', 'true');
      this.toggle.addEventListener('click', () => this.open());
      if (this.close) this.close.addEventListener('click', () => this.closeMenu());
      $$('.us-mobile-menu__group', this.menu).forEach((group) => this.bindGroup(group));
      window.matchMedia('(min-width: 900px)').addEventListener('change', (event) => {
        if (event.matches) this.closeMenu(false);
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.menu.hasAttribute('open')) this.closeMenu();
      });
    }
    open() {
      if (this.menu.hasAttribute('open')) return;
      this.previousOverflow = document.body.style.overflow;
      this.menu.inert = false;
      this.menu.setAttribute('aria-hidden', 'false');
      this.menu.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
      this.toggle.setAttribute('aria-expanded', 'true');
      this.releaseFocus = trapFocus(this.menu);
    }
    closeMenu(restoreFocus = true) {
      if (!this.menu.hasAttribute('open')) return;
      this.menu.inert = true;
      this.menu.setAttribute('aria-hidden', 'true');
      this.menu.removeAttribute('open');
      document.body.style.overflow = this.previousOverflow;
      this.toggle.setAttribute('aria-expanded', 'false');
      if (this.releaseFocus) this.releaseFocus();
      this.releaseFocus = null;
      if (restoreFocus) this.toggle.focus();
    }
    bindGroup(group) {
      const summary = $('summary', group);
      const content = $('.us-mobile-menu__sub', group);
      if (!summary || !content) return;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let expanded = group.open;
      let animation = null;
      let contentAnimation = null;
      content.inert = !expanded;
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        expanded = !expanded;
        const fromHeight = group.getBoundingClientRect().height;
        const fromOpacity = group.open ? getComputedStyle(content).opacity : '0';
        if (animation) animation.cancel();
        if (contentAnimation) contentAnimation.cancel();
        animation = null;
        contentAnimation = null;
        group.style.overflow = '';
        content.inert = !expanded;
        group.toggleAttribute('data-menu-closing', !expanded);
        if (reducedMotion.matches || typeof group.animate !== 'function') {
          group.open = expanded;
          group.removeAttribute('data-menu-closing');
          return;
        }
        // Keep the native details content rendered until its closing motion ends.
        group.open = true;
        const toHeight = expanded ? group.getBoundingClientRect().height : summary.getBoundingClientRect().height;
        group.style.overflow = 'hidden';
        const options = { duration: 180, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' };
        animation = group.animate([{ height: `${fromHeight}px` }, { height: `${toHeight}px` }], options);
        contentAnimation = content.animate([{ opacity: fromOpacity }, { opacity: expanded ? 1 : 0 }], options);
        const currentAnimation = animation;
        animation.onfinish = () => {
          if (animation !== currentAnimation) return;
          group.open = expanded;
          group.style.overflow = '';
          group.removeAttribute('data-menu-closing');
          animation.cancel();
          contentAnimation.cancel();
          animation = null;
          contentAnimation = null;
        };
      });
    }
  }

  // ---------------------------------------------------------------------
  // Mega menu (desktop dropdowns) — explicit state keeps motion, hover,
  // keyboard focus and Escape dismissal in sync.
  // ---------------------------------------------------------------------
  class MegaMenu {
    constructor() {
      this.items = $$('[data-nav-item]');
      if (!this.items.length) return;
      const panelStates = new Map();
      this.items.forEach((item) => {
        const trigger = $('[data-nav-trigger]', item);
        const panel = $('[data-nav-panel]', item);
        if (!trigger || !panel) return;
        let hovered = false;
        let dismissed = false;
        let restoringFocus = false;
        item.setAttribute('data-nav-ready', '');
        const setState = (open) => {
          if (open) panelStates.forEach((setOtherState, other) => {
            if (other !== item) setOtherState(false);
          });
          item.classList.toggle('is-open', open);
          panel.inert = !open;
          panel.setAttribute('aria-hidden', open ? 'false' : 'true');
          trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        };
        panelStates.set(item, setState);
        setState(false);
        item.addEventListener('mouseenter', () => {
          hovered = true;
          dismissed = false;
          setState(true);
        });
        item.addEventListener('mouseleave', () => {
          hovered = false;
          if (dismissed || !item.contains(document.activeElement)) setState(false);
        });
        item.addEventListener('focusin', () => {
          if (restoringFocus) return;
          dismissed = false;
          setState(true);
        });
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget) && (!hovered || dismissed)) setState(false);
        });
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            dismissed = true;
            setState(false);
            restoringFocus = true;
            trigger.focus({ preventScroll: true });
            restoringFocus = false;
          }
        });
      });
    }
  }

  // ---------------------------------------------------------------------
  // Cart drawer
  // ---------------------------------------------------------------------
  class CartDrawer {
    constructor() {
      this.drawer = $('[data-cart-drawer]');
      if (!this.drawer) return;
      this.body = $('[data-cart-drawer-body]', this.drawer);
      this.foot = $('[data-cart-drawer-foot]', this.drawer);
      this.bubble = $('[data-cart-count]');
      this.releaseFocus = null;

      this.drawer.addEventListener('click', (e) => {
        if (e.target === this.drawer) this.close();
      });
      $$('[data-cart-drawer-close]', this.drawer).forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open'))
          this.close();
      });

      $$('[data-open-cart]').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        })
      );

      // Delegated handlers for line interactions
      this.drawer.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          this.updateLine(removeBtn.dataset.lineKey, 0);
          return;
        }
        const incBtn = e.target.closest('[data-qty-inc]');
        if (incBtn) {
          const input = incBtn.parentElement.querySelector('[data-cart-qty]');
          if (input) {
            input.value = parseInt(input.value || '0', 10) + 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }
        const decBtn = e.target.closest('[data-qty-dec]');
        if (decBtn) {
          const input = decBtn.parentElement.querySelector('[data-cart-qty]');
          if (input) {
            const next = Math.max(0, parseInt(input.value || '0', 10) - 1);
            input.value = next;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
      this.drawer.addEventListener(
        'change',
        debounce((e) => {
          const input = e.target.closest('[data-cart-qty]');
          if (input)
            this.updateLine(input.dataset.lineKey, parseInt(input.value, 10) || 0);
        }, 250)
      );

      this.refresh();
    }
    async open() {
      await this.refresh();
      this.drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this.releaseFocus = trapFocus(this.drawer);
    }
    close() {
      this.drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      if (this.releaseFocus) this.releaseFocus();
    }
    async refresh() {
      try {
        const res = await fetch(
          `${window.Shopify.routes.root}?section_id=cart-drawer`
        );
        const text = await res.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const newDrawer = $('[data-cart-drawer]', html);
        if (newDrawer) {
          const newBody = $('[data-cart-drawer-body]', newDrawer);
          const newFoot = $('[data-cart-drawer-foot]', newDrawer);
          if (this.body && newBody) {
            this.body.replaceWith(newBody);
            this.body = newBody;
          }
          if (newFoot) {
            if (this.foot) {
              this.foot.replaceWith(newFoot);
            } else {
              $('.us-drawer__panel', this.drawer).appendChild(newFoot);
            }
            this.foot = newFoot;
          } else if (this.foot) {
            this.foot.remove();
            this.foot = null;
          }
        }
        const cartRes = await fetch(`${window.Shopify.routes.root}cart.js`);
        // Password-protected storefronts reject cart JSON until the gate is
        // unlocked. That is an expected state, not a storefront error.
        if (cartRes.status === 401) return;
        if (!cartRes.ok) throw new Error(`Cart request failed: ${cartRes.status}`);
        const cartText = await cartRes.text();
        if (cartText.trim()) {
          const cart = JSON.parse(cartText);
          this.updateBubble(cart.item_count);
        }
      } catch (err) {
        console.error('Cart refresh failed', err);
      }
    }
    updateBubble(count) {
      if (this.bubble) {
        this.bubble.textContent = count;
        this.bubble.dataset.count = count;
      }
    }
    async addItem(formData) {
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add to cart');
      }
      await this.refresh();
      this.open();
      return res;
    }
    async updateLine(key, qty) {
      const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty }),
      });
      if (res.ok) await this.refresh();
    }
  }

  // ---------------------------------------------------------------------
  // Quantity stepper (PDP and any [data-qty-inc]/[data-qty-dec] outside the
  // cart drawer, which has its own delegated handler above).
  // ---------------------------------------------------------------------
  class QtyStepper {
    constructor() {
      document.addEventListener('click', (e) => {
        const inc = e.target.closest('[data-qty-inc]');
        if (inc && !inc.closest('[data-cart-drawer]')) {
          const input = inc.parentElement.querySelector(
            'input[name="quantity"], input.us-qty__input'
          );
          if (input) {
            const min = parseInt(input.min || '0', 10);
            const next = Math.max(min, parseInt(input.value || '1', 10) + 1);
            input.value = next;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        const dec = e.target.closest('[data-qty-dec]');
        if (dec && !dec.closest('[data-cart-drawer]')) {
          const input = dec.parentElement.querySelector(
            'input[name="quantity"], input.us-qty__input'
          );
          if (input) {
            const min = parseInt(input.min || '0', 10);
            const next = Math.max(min, parseInt(input.value || '1', 10) - 1);
            input.value = next;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }
  }

  // ---------------------------------------------------------------------
  // Product gallery — clicking a thumb shows its matching frame.
  // ---------------------------------------------------------------------
  class ProductGallery {
    constructor(root) {
      this.root = root;
      this.frames = $$('.us-product__media-frame', root);
      this.thumbs = $$('.us-product__media-thumb', root);
      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => this.select(thumb.dataset.mediaId))
      );
    }
    select(mediaId) {
      this.frames.forEach((f) =>
        f.classList.toggle('is-active', f.dataset.mediaId === mediaId)
      );
      this.thumbs.forEach((t) => {
        const active = t.dataset.mediaId === mediaId;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }
  }

  // ---------------------------------------------------------------------
  // Product form (variant picker + add-to-cart)
  // ---------------------------------------------------------------------
  class ProductForm {
    constructor(form) {
      this.form = form;
      this.section = form.closest('[data-product-section]');
      if (!this.section) return;

      const jsonEl = $('[data-product-json]', this.section);
      this.product = jsonEl ? JSON.parse(jsonEl.textContent) : null;

      this.variantInput = $('[data-variant-id]', form);
      this.optionInputs = $$('[data-option-input]', this.section);
      this.submit = form.querySelector('[data-add-to-cart]');
      this.submitLabel = form.querySelector('[data-add-to-cart-label]');
      this.errorEl = $('[data-product-error]', this.section);
      this.regularPrice = $('[data-product-regular-price]', this.section);
      this.salePrice = $('[data-product-sale-price]', this.section);
      this.comparePrice = $('[data-product-compare-price]', this.section);
      this.soldOutBadge = $('[data-product-sold-out]', this.section);
      this.skuRow = $('[data-product-sku]', this.section);
      this.skuValue = $('[data-product-sku-value]', this.section);

      this.optionInputs.forEach((el) =>
        el.addEventListener('change', () => this.handleOptionChange())
      );
      form.addEventListener('submit', (e) => this.handleSubmit(e));
      // Single-variant products have no option inputs. Their server-rendered
      // state is already correct and must not be overwritten as unavailable.
      if (this.optionInputs.length) this.handleOptionChange();
    }

    selectedOptions() {
      // Read currently-checked radio for each option position
      const opts = [];
      this.optionInputs.forEach((input) => {
        const pos = parseInt(input.dataset.optionPosition, 10);
        if (input.checked) opts[pos] = input.value;
      });
      return opts;
    }

    findVariant() {
      if (!this.product) return null;
      const opts = this.selectedOptions();
      return this.product.variants.find((v) =>
        v.options.every((opt, i) => opts[i] === opt)
      );
    }

    handleOptionChange() {
      // Update "picked" mirror labels
      const opts = this.selectedOptions();
      $$('[data-picked-label]', this.section).forEach((el) => {
        const idx = parseInt(el.dataset.pickedLabel, 10);
        el.textContent = opts[idx] || '';
      });

      const variant = this.findVariant();
      if (!variant) {
        this.setUnavailable();
        return;
      }

      if (this.variantInput) this.variantInput.value = variant.id;

      this.updateVariantDetails(variant);

      // Update URL with selected variant (no page reload)
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      } catch (_) {}

      // Update buy button
      if (this.submit && this.submitLabel) {
        if (variant.available) {
          this.submit.disabled = false;
          this.submitLabel.textContent =
            window.theme?.strings?.addToCart || 'Add to cart';
        } else {
          this.submit.disabled = true;
          this.submitLabel.textContent =
            window.theme?.strings?.soldOut || 'Sold out';
        }
      }
    }

    updateVariantDetails(variant) {
      const onSale = Boolean(variant.comparePrice);
      if (this.regularPrice) {
        this.regularPrice.textContent = variant.price;
        this.regularPrice.hidden = onSale;
      }
      if (this.salePrice) {
        this.salePrice.textContent = variant.price;
        this.salePrice.hidden = !onSale;
      }
      if (this.comparePrice) {
        this.comparePrice.textContent = variant.comparePrice || '';
        this.comparePrice.hidden = !onSale;
      }
      if (this.soldOutBadge) this.soldOutBadge.hidden = variant.available;

      if (this.skuRow && this.skuValue) {
        this.skuValue.textContent = variant.sku || '';
        this.skuRow.hidden = !variant.sku;
      }
    }

    setUnavailable() {
      if (this.submit && this.submitLabel) {
        this.submit.disabled = true;
        this.submitLabel.textContent =
          window.theme?.strings?.unavailable || 'Unavailable';
      }
    }

    async handleSubmit(e) {
      e.preventDefault();
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
      if (!this.submit) return;
      this.submit.disabled = true;
      const original = this.submitLabel ? this.submitLabel.textContent : '';
      if (this.submitLabel)
        this.submitLabel.textContent =
          window.theme?.strings?.adding || 'Adding…';
      try {
        const formData = new FormData(this.form);
        if (window.cartDrawer) {
          await window.cartDrawer.addItem(formData);
        } else {
          this.form.submit();
          return;
        }
        if (this.submitLabel)
          this.submitLabel.textContent =
            window.theme?.strings?.added || 'Added';
        setTimeout(() => {
          this.submit.disabled = false;
          if (this.submitLabel) this.submitLabel.textContent = original;
        }, 1500);
      } catch (err) {
        console.error(err);
        if (this.errorEl) {
          this.errorEl.textContent = err.message;
          this.errorEl.hidden = false;
        }
        this.submit.disabled = false;
        if (this.submitLabel) this.submitLabel.textContent = original;
      }
    }
  }

  // ---------------------------------------------------------------------
  // Quick-add (single-variant products in grids)
  // ---------------------------------------------------------------------
  class QuickAdd {
    constructor() {
      document.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-quick-add]');
        if (!btn) return;
        const id = btn.dataset.quickAdd;
        if (!id || !window.cartDrawer) return;
        e.preventDefault();
        const original = btn.innerHTML;
        btn.disabled = true;
        try {
          const form = new FormData();
          form.append('id', id);
          form.append('quantity', '1');
          await window.cartDrawer.addItem(form);
          btn.textContent = btn.dataset.addedLabel || 'Added';
          setTimeout(() => {
            btn.innerHTML = original;
            btn.disabled = false;
          }, 1200);
        } catch (err) {
          console.error('Quick-add failed', err);
          btn.disabled = false;
        }
      });
    }
  }

  // ---------------------------------------------------------------------
  // Predictive search
  // ---------------------------------------------------------------------
  class PredictiveSearch {
    constructor() {
      this.form = $('[data-predictive-search-form]');
      if (!this.form) return;
      this.input = $('input[type="search"]', this.form);
      this.results = $('[data-predictive-search-results]');
      this.container = $('[data-search-modal]');
      this.openers = $$('[data-search-open]');
      this.closers = $$('[data-search-close]');
      this.opener = null;
      this.previousOverflow = '';
      this.releaseFocus = null;
      this.requestController = null;
      this.requestVersion = 0;

      this.openers.forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(btn);
        })
      );
      this.closers.forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      if (this.container) {
        this.container.addEventListener('cancel', (e) => {
          e.preventDefault();
          this.close();
        });
        this.container.addEventListener('close', () => this.reset());
        this.container.addEventListener('click', (e) => {
          if (e.target === this.container) this.close();
        });
        this.container.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            this.close();
          }
        });
      }
      if (this.input) {
        this.input.addEventListener(
          'input',
          debounce(() => this.search(), 200)
        );
      }
    }
    open(opener) {
      if (!this.container || this.container.open) return;
      this.opener = opener || document.activeElement;
      this.previousOverflow = document.body.style.overflow;
      this.container.classList.add('is-open');
      this.container.removeAttribute('hidden');
      if (typeof this.container.showModal === 'function') {
        this.container.showModal();
      } else {
        this.container.setAttribute('open', '');
      }
      this.releaseFocus = trapFocus(this.container);
      document.body.style.overflow = 'hidden';
      if (this.input) this.input.focus();
      this.search();
    }
    close() {
      if (!this.container) return;
      if (this.container.open && typeof this.container.close === 'function') {
        this.container.close();
      } else {
        this.container.removeAttribute('open');
      }
      this.reset();
    }
    reset() {
      if (!this.container || !this.container.classList.contains('is-open')) return;
      this.container.classList.remove('is-open');
      this.container.setAttribute('hidden', '');
      document.body.style.overflow = this.previousOverflow;
      if (this.releaseFocus) this.releaseFocus();
      this.releaseFocus = null;
      if (this.requestController) this.requestController.abort();
      this.requestVersion += 1;
      if (this.results) this.results.setAttribute('aria-busy', 'false');
      if (this.opener && this.opener.isConnected) this.opener.focus();
      this.opener = null;
    }
    async search() {
      if (!this.input || !this.container || !this.container.open) return;
      const q = this.input.value.trim();
      const requestVersion = ++this.requestVersion;
      if (this.requestController) this.requestController.abort();
      if (!q || q.length < 2) {
        if (this.results) {
          this.results.innerHTML = '';
          this.results.setAttribute('aria-busy', 'false');
        }
        return;
      }
      this.requestController = new AbortController();
      if (this.results) this.results.setAttribute('aria-busy', 'true');
      try {
        const url = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(
          q
        )}&resources[type]=product,article,page&resources[limit]=8&section_id=predictive-search`;
        const res = await fetch(url, { signal: this.requestController.signal });
        if (!res.ok) throw new Error(`Search returned ${res.status}`);
        const text = await res.text();
        if (requestVersion !== this.requestVersion || q !== this.input.value.trim()) return;
        const html = new DOMParser().parseFromString(text, 'text/html');
        const inner = $('[data-predictive-search-results]', html);
        if (!inner) throw new Error('Search results were unavailable');
        if (this.results) this.results.innerHTML = inner.innerHTML;
      } catch (err) {
        if (
          err.name === 'AbortError' ||
          requestVersion !== this.requestVersion ||
          q !== this.input.value.trim()
        ) return;
        if (this.results) {
          const link = document.createElement('a');
          const url = new URL(this.form.action, window.location.origin);
          url.searchParams.set('q', q);
          url.searchParams.set('options[prefix]', 'last');
          link.href = url.toString();
          link.className = 'us-predictive__item';
          link.textContent = this.form.dataset.searchLabel;
          this.results.replaceChildren(link);
        }
        console.error('Predictive search failed', err);
      } finally {
        if (this.results && requestVersion === this.requestVersion) {
          this.results.setAttribute('aria-busy', 'false');
        }
      }
    }
  }

  // ---------------------------------------------------------------------
  // Sticky add-to-cart (mobile)
  // ---------------------------------------------------------------------
  class StickyATC {
    constructor(root) {
      const trigger = root.querySelector('[data-sticky-atc-trigger]');
      const realAddBtn = document.querySelector('[data-add-to-cart]');
      const sentinel = document.querySelector('.us-product__buy');
      if (!trigger || !realAddBtn || !sentinel) return;

      trigger.addEventListener('click', () => realAddBtn.click());

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const show = !entry.isIntersecting;
            root.classList.toggle('is-visible', show);
            if (show) {
              root.hidden = false;
              document.body.classList.add('has-sticky-atc');
            } else {
              document.body.classList.remove('has-sticky-atc');
            }
          });
        },
        { rootMargin: '0px 0px -120px 0px' }
      );
      io.observe(sentinel);
    }
  }

  // ---------------------------------------------------------------------
  // Bootstrap
  // ---------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', () => {
    new MobileMenu();
    new MegaMenu();
    window.cartDrawer = new CartDrawer();
    new QuickAdd();
    new PredictiveSearch();
    new QtyStepper();
    $$('[data-product-form]').forEach((form) => new ProductForm(form));
    // Also bind any form inside a [data-product-section] without an explicit hook
    $$('[data-product-section] form[action*="/cart/add"]').forEach((form) => {
      if (!form.hasAttribute('data-product-form')) {
        form.setAttribute('data-product-form', '');
        new ProductForm(form);
      }
    });
    $$('[data-product-gallery]').forEach((g) => new ProductGallery(g));
    $$('[data-sticky-atc]').forEach((s) => new StickyATC(s));
  });
})();
